import {
  BacklinkDisplayType,
  BacklinkDisplayTypeMap,
  type AiFieldRunMetadata,
  ContentFieldToDisplayedDetailTypeMap,
  type DateFormat,
  DateFormatPossibleValues,
  DisplayedDetailType,
  DisplayedDetailTypeMap,
  DisplayedDetailTypeToContentFieldMap,
  type Field,
  type FieldJSON,
  FieldType,
  MediaFormat,
  type MutableField,
  type NumberFormat,
  NumberFormatMap,
  PageSubType,
  ParentPageOption,
  ParentPageType,
  type TimeFormat,
  TimeFormatPossibleValues,
  YBacklinkDisplayType,
  YBacklinkDisplayTypeMap,
  YBacklinkDisplayTypePossibleValues,
  YDisplayedDetailType,
  YDisplayedDetailTypeMap,
  YDisplayedDetailTypePossibleValues,
  YNumberFormat,
  YNumberFormatMap,
  YNumberFormatPossibleValues
} from '@databases/model/field';
import { type Op } from 'quill-delta';
import * as Y from 'yjs';

import { AI_ACTORS } from './ai-metadata';
import { LazyMapElement } from './helpers/lazy-map-element';
import { SynchronizedMapElement } from './helpers/synchronized-map-element';
import { SynchronizedRecord } from './helpers/synchronized-record';
import { type DisposableElement } from './helpers/types';
import { YCustomNumberFormat } from './y-custom-number-format';
import { YSelectOptions } from './y-select-options';

export const Properties = {
  // shared properties
  description: 'd',
  name: 'n',
  settingDefaultEnabled: 'sde',
  settingMulti: 'sm',
  settingShowDescription: 'sd',
  type: 't',
  width: 'w',
  // date field
  settingDateFormat: 'sdf',
  settingEnableDateRange: 'sedr',
  settingTimeFormat: 'stf',
  // entry backlink field
  settingBacklinkDisplayType: 'sbdt',
  // entry details, jira issue details, page details, page label, and page status fields
  settingFieldId: 'sfi',
  // entry details field
  settingEntryFieldField: 'seff',
  // entry link & entry backlink field
  settingDatabaseId: 'sdi',
  // media field
  settingMediaFormat: 'smf',
  // number field
  settingCustomNumberFormat: 'scnf',
  settingNumberFormat: 'snf',
  // jira issue field
  settingJiraServerId: 'sjsi',
  settingCloudId: 'sci',
  // jira issue details field
  settingJiraField: 'sjf',
  // page details field
  settingContentField: 'scf',
  settingIncludeVersionComment: 'sivc',
  // page label & page status fields
  settingAllowEdit: 'sae',
  // page link field
  settingCreatable: 'sc',
  settingDisplayEntryOnPage: 'sdep',
  settingIsParentPageSourceDatabase: 'sippsd',
  settingPageSubType: 'spst',
  settingPageTemplateId: 'spti',
  settingParentPage: 'spp', // TODO: Rename as parentPageId
  settingParentPageOption: 'sppo',
  settingParentPageType: 'sppt', // Always stored as `PAGE` but should be properly set to Databases where applicable and replace boolean type `settingIsParentPageSourceDatabase`
  // smart links field
  settingResolve: 'sr',
  // tag field
  settingSelectOptions: 'sso',
  // user field
  settingMentionNotificationEnabled: 'smne',
  aiLastRunAt: 'ai_lra',
  aiLastRunBy: 'ai_lrb',
  aiLastRunUserAccountId: 'ai_lrb_aid',
  // deprecated
  settingDisplayedDetail: 'sdd'
};

const hasAiFieldRunMetadata = (metadata: AiFieldRunMetadata): boolean =>
  metadata.lastRunAt !== undefined ||
  metadata.lastRunBy !== undefined ||
  metadata.lastRunUserAccountId !== undefined;

export enum YFieldType {
  TEXT = 't',
  AI_SUMMARY = 'as',
  NUMBER = 'n',
  DATE = 'd',
  SELECT = 's',
  USER = 'u',
  ATTACHMENT = 'i', // renamed from IMAGE to ATTACHMENT but kept value as 'i' for backwards compatibility
  CHECKBOX = 'c',
  PAGE_LINK = 'pl',
  PAGE_LABELS = 'pla',
  PAGE_STATUS = 'ps',
  PAGE_DETAILS = 'pd',
  HYPERLINK = 'h',
  ENTRY_LINK = 'el',
  ENTRY_DETAILS = 'ed',
  BACKLINK = 'b',
  JIRA_ISSUE = 'ji',
  JIRA_ISSUE_DETAILS = 'jid'
}

export const YFieldMapping: Record<YFieldType, FieldType> = {
  [YFieldType.TEXT]: FieldType.Text,
  [YFieldType.AI_SUMMARY]: FieldType.AiSummary,
  [YFieldType.NUMBER]: FieldType.Number,
  [YFieldType.DATE]: FieldType.Date,
  [YFieldType.SELECT]: FieldType.Select,
  [YFieldType.USER]: FieldType.User,
  [YFieldType.ATTACHMENT]: FieldType.Attachment,
  [YFieldType.CHECKBOX]: FieldType.Checkbox,
  [YFieldType.PAGE_LINK]: FieldType.PageLink,
  [YFieldType.PAGE_LABELS]: FieldType.PageLabels,
  [YFieldType.PAGE_STATUS]: FieldType.PageStatus,
  [YFieldType.PAGE_DETAILS]: FieldType.PageDetails,
  [YFieldType.HYPERLINK]: FieldType.Hyperlink,
  [YFieldType.ENTRY_LINK]: FieldType.EntryLink,
  [YFieldType.ENTRY_DETAILS]: FieldType.EntryDetails,
  [YFieldType.BACKLINK]: FieldType.Backlink,
  [YFieldType.JIRA_ISSUE]: FieldType.JiraIssue,
  [YFieldType.JIRA_ISSUE_DETAILS]: FieldType.JiraIssueDetails
};

export const FieldMapping: Record<FieldType, YFieldType> = {
  [FieldType.Text]: YFieldType.TEXT,
  [FieldType.AiSummary]: YFieldType.AI_SUMMARY,
  [FieldType.Number]: YFieldType.NUMBER,
  [FieldType.Date]: YFieldType.DATE,
  [FieldType.Select]: YFieldType.SELECT,
  [FieldType.User]: YFieldType.USER,
  [FieldType.Attachment]: YFieldType.ATTACHMENT,
  [FieldType.Checkbox]: YFieldType.CHECKBOX,
  [FieldType.PageLink]: YFieldType.PAGE_LINK,
  [FieldType.PageLabels]: YFieldType.PAGE_LABELS,
  [FieldType.PageStatus]: YFieldType.PAGE_STATUS,
  [FieldType.PageDetails]: YFieldType.PAGE_DETAILS,
  [FieldType.Hyperlink]: YFieldType.HYPERLINK,
  [FieldType.EntryLink]: YFieldType.ENTRY_LINK,
  [FieldType.EntryDetails]: YFieldType.ENTRY_DETAILS,
  [FieldType.Backlink]: YFieldType.BACKLINK,
  [FieldType.JiraIssue]: YFieldType.JIRA_ISSUE,
  [FieldType.JiraIssueDetails]: YFieldType.JIRA_ISSUE_DETAILS
};

export const YFieldTypeValues = Object.values(YFieldType);

export class YField
  extends SynchronizedRecord
  implements Field, MutableField, DisposableElement
{
  private nameElement: SynchronizedMapElement<Y.Text>;
  private descriptionElement: SynchronizedMapElement<Y.Text>;
  #customNumberFormat: LazyMapElement<YCustomNumberFormat>;
  #selectOptions: YSelectOptions;

  constructor(data: Y.Map<unknown>) {
    super(data);

    this.#customNumberFormat = new LazyMapElement(
      data,
      Properties.settingCustomNumberFormat,
      (input) => {
        if (input instanceof Y.Map) {
          return new YCustomNumberFormat(input);
        }
      },
      () => new Y.Map(),
      (input, attach) => {
        return new YCustomNumberFormat(input, attach);
      }
    );

    this.#selectOptions = new YSelectOptions(
      new SynchronizedMapElement<Y.Array<unknown>>(
        data,
        Properties.settingSelectOptions,
        (input) => (input instanceof Y.Array ? input : undefined),
        () => new Y.Array()
      )
    );

    this.nameElement = new SynchronizedMapElement(
      data,
      Properties.name,
      (input) => (input instanceof Y.Text ? input : undefined),
      () => new Y.Text()
    );

    this.descriptionElement = new SynchronizedMapElement(
      data,
      Properties.description,
      (input) => (input instanceof Y.Text ? input : undefined),
      () => new Y.Text()
    );
  }

  get dateFormat(): DateFormat | undefined {
    return this.getStringEnumOptional(
      Properties.settingDateFormat,
      DateFormatPossibleValues
    );
  }

  set dateFormat(val: DateFormat | undefined) {
    this.set(Properties.settingDateFormat, val);
  }

  get enableDateRange(): boolean | undefined {
    return this.getBoolean(Properties.settingEnableDateRange, undefined);
  }

  set enableDateRange(val: boolean) {
    this.set(Properties.settingEnableDateRange, val);
  }

  get timeFormat(): TimeFormat | undefined {
    return this.getStringEnumOptional(
      Properties.settingTimeFormat,
      TimeFormatPossibleValues
    );
  }

  set timeFormat(val: TimeFormat | undefined) {
    this.set(Properties.settingTimeFormat, val);
  }

  get description(): string {
    return this.descriptionElement.value?.toString() ?? '';
  }

  applyDescriptionDelta(delta: Op[]): void {
    this.descriptionElement.valueRequired.applyDelta(delta);
  }

  get name(): string {
    return this.nameElement.value?.toString() ?? '';
  }

  applyNameDelta(delta: Op[]): void {
    this.nameElement.valueRequired.applyDelta(delta);
  }

  get type(): FieldType {
    return YFieldMapping[
      this.getStringEnum<typeof YFieldTypeValues>(
        Properties.type,
        YFieldTypeValues,
        YFieldType.TEXT
      )
    ];
  }

  set type(newType: FieldType) {
    this.set(Properties.type, FieldMapping[newType]);
  }

  get showDescription(): boolean {
    return this.getBoolean(Properties.settingShowDescription, false);
  }

  set showDescription(val: boolean) {
    this.set(Properties.settingShowDescription, val);
  }

  get width(): number | undefined {
    return this.getNumber(Properties.width, undefined);
  }

  set width(value: number | undefined) {
    this.set(Properties.width, value);
  }

  get multi(): boolean {
    return this.getBoolean(Properties.settingMulti, true);
  }

  set multi(val: boolean) {
    this.set(Properties.settingMulti, val);
  }

  get resolve(): boolean {
    return this.getBoolean(Properties.settingResolve, true);
  }

  set resolve(val: boolean) {
    this.set(Properties.settingResolve, val);
  }

  get allowEdit(): boolean {
    return this.getBoolean(Properties.settingAllowEdit, true);
  }

  set allowEdit(val: boolean) {
    this.set(Properties.settingAllowEdit, val);
  }

  get databaseId(): string | undefined {
    return this.getString(Properties.settingDatabaseId, undefined);
  }

  set databaseId(val: string | undefined) {
    this.set(Properties.settingDatabaseId, val);
  }

  get contentField(): string | undefined {
    return this.getString(
      Properties.settingContentField,
      DisplayedDetailTypeToContentFieldMap[this.displayedDetail]
    );
  }

  set contentField(val: string | undefined) {
    this.set(Properties.settingContentField, val);
    if (val && Object.keys(ContentFieldToDisplayedDetailTypeMap).includes(val)) {
      const yDisplayedDetailType = ContentFieldToDisplayedDetailTypeMap[val];
      this.set(
        Properties.settingDisplayedDetail,
        DisplayedDetailTypeMap[yDisplayedDetailType]
      );
    }
  }

  get displayedDetail(): DisplayedDetailType {
    const yDisplayedDetailType = this.getStringEnum(
      Properties.settingDisplayedDetail,
      YDisplayedDetailTypePossibleValues,
      YDisplayedDetailType.CREATED_AT
    );

    return YDisplayedDetailTypeMap[yDisplayedDetailType];
  }

  set displayedDetail(val: DisplayedDetailType) {
    const yDisplayedDetailType = DisplayedDetailTypeMap[val];
    this.set(Properties.settingDisplayedDetail, yDisplayedDetailType);
    if (val && Object.keys(DisplayedDetailTypeToContentFieldMap).includes(val)) {
      const contentField = DisplayedDetailTypeToContentFieldMap[val];
      this.set(Properties.settingContentField, contentField);
    }
  }

  get backlinkDisplayType(): BacklinkDisplayType {
    const yBacklinkDisplayType = this.getStringEnum(
      Properties.settingBacklinkDisplayType,
      YBacklinkDisplayTypePossibleValues,
      YBacklinkDisplayType.AS_LINK
    );

    return YBacklinkDisplayTypeMap[yBacklinkDisplayType];
  }

  set backlinkDisplayType(val: BacklinkDisplayType) {
    const yBacklinkDisplayType = BacklinkDisplayTypeMap[val];
    this.set(Properties.settingBacklinkDisplayType, yBacklinkDisplayType);
  }

  get fieldId(): string | undefined {
    return this.getString(Properties.settingFieldId, undefined);
  }

  set fieldId(val: string | undefined) {
    this.set(Properties.settingFieldId, val);
  }

  get includeVersionComment(): boolean | undefined {
    return this.getBoolean(Properties.settingIncludeVersionComment, undefined);
  }

  set includeVersionComment(val: boolean | undefined) {
    this.set(Properties.settingIncludeVersionComment, val);
  }

  get creatable(): boolean | undefined {
    return this.getBoolean(Properties.settingCreatable, undefined);
  }

  set creatable(val: boolean | undefined) {
    this.set(Properties.settingCreatable, val);
  }

  get pageTemplateId(): string | undefined {
    return this.getString(Properties.settingPageTemplateId, undefined);
  }

  set pageTemplateId(val: string | undefined) {
    this.set(Properties.settingPageTemplateId, val);
  }

  get pageSubType(): PageSubType {
    return this.getStringEnum(
      Properties.settingPageSubType,
      Object.values(PageSubType),
      PageSubType.LIVE
    );
  }

  set pageSubType(val: PageSubType) {
    this.set(Properties.settingPageSubType, val);
  }

  get mediaFormat(): MediaFormat {
    return this.getStringEnum(
      Properties.settingMediaFormat,
      Object.values(MediaFormat),
      MediaFormat.AUTOFILL
    );
  }

  set mediaFormat(val: MediaFormat) {
    this.set(Properties.settingMediaFormat, val);
  }

  get parentPage(): string | undefined {
    return this.getString(Properties.settingParentPage, undefined);
  }

  /**
   * Type reset via migration
   */
  set parentPage(val: string | undefined) {
    this.set(Properties.settingParentPage, val);
  }

  get parentPageOption(): ParentPageOption {
    return this.getStringEnum(
      Properties.settingParentPageOption,
      Object.values(ParentPageOption),
      ParentPageOption.FIXED
    );
  }

  set parentPageOption(val: ParentPageOption) {
    this.set(Properties.settingParentPageOption, val);
  }

  get isParentPageSourceDatabase(): boolean | undefined {
    return this.getBoolean(Properties.settingIsParentPageSourceDatabase, true);
  }

  set isParentPageSourceDatabase(val: boolean | undefined) {
    this.set(Properties.settingIsParentPageSourceDatabase, val);
  }

  get displayEntryOnPage(): boolean | undefined {
    return this.getBoolean(Properties.settingDisplayEntryOnPage, true);
  }

  set displayEntryOnPage(val: boolean | undefined) {
    this.set(Properties.settingDisplayEntryOnPage, val);
  }

  get parentPageType(): ParentPageType {
    return this.getStringEnum(
      Properties.settingParentPageType,
      Object.values(ParentPageType),
      ParentPageType.PAGE
    );
  }

  set parentPageType(val: ParentPageType) {
    this.set(Properties.settingParentPageType, val);
  }

  get entryFieldField(): string | undefined {
    return this.getString(Properties.settingEntryFieldField, undefined);
  }

  set entryFieldField(val: string | undefined) {
    this.set(Properties.settingEntryFieldField, val);
  }

  get jiraField(): string | undefined {
    return this.getString(Properties.settingJiraField, undefined);
  }

  set jiraField(val: string | undefined) {
    this.set(Properties.settingJiraField, val);
  }

  get jiraServerId(): string | undefined {
    return this.getString(Properties.settingJiraServerId, undefined);
  }

  set jiraServerId(val: string | undefined) {
    this.set(Properties.settingJiraServerId, val);
  }

  get cloudId(): string | undefined {
    return this.getString(Properties.settingCloudId, undefined);
  }

  set cloudId(val: string | undefined) {
    this.set(Properties.settingCloudId, val);
  }

  get numberFormat(): NumberFormat {
    const yNumberFormat = this.getStringEnum(
      Properties.settingNumberFormat,
      YNumberFormatPossibleValues,
      YNumberFormat.NUMBER
    );

    return YNumberFormatMap[yNumberFormat];
  }

  set numberFormat(val: NumberFormat) {
    const yNumberFormat = NumberFormatMap[val];
    this.set(Properties.settingNumberFormat, yNumberFormat);
  }

  get customNumberFormat() {
    return this.#customNumberFormat.value;
  }

  get selectOptions(): YSelectOptions {
    return this.#selectOptions;
  }

  get defaultEnabled(): boolean {
    return this.getBoolean(Properties.settingDefaultEnabled, false);
  }

  set defaultEnabled(val: boolean) {
    this.set(Properties.settingDefaultEnabled, val);
  }

  get mentionNotificationEnabled(): boolean {
    return this.getBoolean(Properties.settingMentionNotificationEnabled, true);
  }

  set mentionNotificationEnabled(val: boolean) {
    this.set(Properties.settingMentionNotificationEnabled, val);
  }

  get aiRunMetadata(): AiFieldRunMetadata | undefined {
    const metadata = {
      lastRunAt: this.getString(Properties.aiLastRunAt, undefined),
      lastRunBy: this.getStringEnumOptional(Properties.aiLastRunBy, AI_ACTORS),
      lastRunUserAccountId: this.getString(Properties.aiLastRunUserAccountId, undefined)
    };

    return hasAiFieldRunMetadata(metadata) ? metadata : undefined;
  }

  set aiRunMetadata(val: AiFieldRunMetadata | undefined) {
    this.set(Properties.aiLastRunAt, val?.lastRunAt);
    this.set(Properties.aiLastRunBy, val?.lastRunBy);
    this.set(Properties.aiLastRunUserAccountId, val?.lastRunUserAccountId);
  }

  public toJSON(): FieldJSON {
    return {
      description: this.description,
      showDescription: this.showDescription,
      name: this.name,
      type: this.type,
      width: this.width,
      resolve: this.resolve,
      multi: this.multi,
      allowEdit: this.allowEdit,
      databaseId: this.databaseId,
      displayedDetail: this.displayedDetail,
      contentField: this.contentField,
      backlinkDisplayType: this.backlinkDisplayType,
      fieldId: this.fieldId,
      includeVersionComment: this.includeVersionComment,
      creatable: this.creatable,
      entryFieldField: this.entryFieldField,
      pageTemplateId: this.pageTemplateId,
      pageSubType: this.pageSubType,
      parentPage: this.parentPage,
      parentPageOption: this.parentPageOption,
      isParentPageSourceDatabase: this.isParentPageSourceDatabase,
      displayEntryOnPage: this.displayEntryOnPage,
      parentPageType: this.parentPageType,
      jiraField: this.jiraField,
      jiraServerId: this.jiraServerId,
      cloudId: this.cloudId,
      numberFormat: this.numberFormat,
      customNumberFormat: this.customNumberFormat.toJSON(),
      mediaFormat: this.mediaFormat,
      selectOptions: this.selectOptions.toJSON(),
      defaultEnabled: this.defaultEnabled,
      mentionNotificationEnabled: this.mentionNotificationEnabled,
      dateFormat: this.dateFormat,
      timeFormat: this.timeFormat,
      enableDateRange: this.enableDateRange,
      aiRunMetadata: this.aiRunMetadata
    };
  }

  static fromJSON(data: Pick<FieldJSON, 'type'> & FieldJSON): YField {
    return new YField(
      new Y.Map([
        [Properties.name, new Y.Text(data.name)],
        [Properties.type, FieldMapping[data.type]],
        [Properties.description, new Y.Text(data.description)],
        [Properties.width, data.width],

        [Properties.settingShowDescription, data.showDescription],
        [Properties.settingMulti, data.multi],
        [Properties.settingResolve, data.resolve],
        [Properties.settingAllowEdit, data.allowEdit],
        [Properties.settingDatabaseId, data.databaseId],
        [
          Properties.settingBacklinkDisplayType,
          BacklinkDisplayTypeMap[data.backlinkDisplayType || BacklinkDisplayType.AS_LINK]
        ],
        [
          Properties.settingDisplayedDetail,
          DisplayedDetailTypeMap[data.displayedDetail || DisplayedDetailType.CREATED_AT]
        ],
        [Properties.settingFieldId, data.fieldId],
        [Properties.settingContentField, data.contentField],
        [Properties.settingIncludeVersionComment, data.includeVersionComment],
        [Properties.settingCreatable, data.creatable],
        [Properties.settingEntryFieldField, data.entryFieldField],
        [Properties.settingPageTemplateId, data.pageTemplateId],
        [Properties.settingPageSubType, data.pageSubType],
        [Properties.settingParentPage, data.parentPage],
        [Properties.settingParentPageOption, data.parentPageOption],
        [Properties.settingIsParentPageSourceDatabase, data.isParentPageSourceDatabase],
        [Properties.settingDisplayEntryOnPage, data.displayEntryOnPage],
        [Properties.settingParentPageType, data.parentPageType],
        [Properties.settingJiraField, data.jiraField],
        [Properties.settingJiraServerId, data.jiraServerId],
        [Properties.settingCloudId, data.cloudId],
        [Properties.settingMediaFormat, data.mediaFormat],
        [
          Properties.settingSelectOptions,
          YSelectOptions.fromJSON(data.selectOptions).unwrap()
        ],
        [Properties.settingNumberFormat, NumberFormatMap[data.numberFormat]],
        [
          Properties.settingCustomNumberFormat,
          YCustomNumberFormat.fromJSON(data.customNumberFormat).data
        ],
        [Properties.settingDefaultEnabled, data.defaultEnabled],
        [Properties.settingMentionNotificationEnabled, data.mentionNotificationEnabled],
        [Properties.settingDateFormat, data.dateFormat],
        [Properties.settingTimeFormat, data.timeFormat],
        [Properties.aiLastRunAt, data.aiRunMetadata?.lastRunAt],
        [Properties.aiLastRunBy, data.aiRunMetadata?.lastRunBy],
        [Properties.aiLastRunUserAccountId, data.aiRunMetadata?.lastRunUserAccountId]
      ])
    );
  }

  dispose(): void {
    this.nameElement.dispose();
    this.descriptionElement.dispose();
  }
}
